import { useCallback, useEffect, useRef } from "react";
import { lazyInitializeNativeContextMenu } from "../lazyFunction";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { type ContextMenuRequestedArguments, type ExtendedHost, getEnhancedHostExtendedHost, } from "owa-config/lib/enhancedHost";
import { isHTMLElement } from "@fluentui/react-components";

export const useInitializeNativeContextMenuEvents = (): void => {
    const contextMenuEvent = useRef<MouseEvent | null>(null);
    const isContextMenuEventTriggered = useRef(false);
    const extendedHost = useRef<ExtendedHost | undefined>(getEnhancedHostExtendedHost());
    const isExtendedHostHandlerSet = useRef(true);
    
    const shouldDisplayNativeContextMenu = useCallback((mouseEvent: MouseEvent): boolean => {
        // If it is an input element, we do not want to show the native context menu
        if (isInputElement(mouseEvent.target) &&
            !mouseEvent.target.readOnly &&
            !mouseEvent.target.disabled &&
            mouseEvent.target.type == "text" &&
            mouseEvent.target.getAttribute("spellcheck") !== "false") {
            return false;
        }
        // If it is a content editable element with spellcheck enabled, we do not want to show the native context menu
        if (isHTMLElement(mouseEvent.target) &&
            mouseEvent.target.isContentEditable &&
            mouseEvent.target.getAttribute("spellcheck") !== "false") {
            // If all conditions are met, we set the contextMenuEvent to null so that the native context menu is not shown
            return false;
        }
        return true;
    }, []);
    
    const onContextMenuRequestedHandler = useCallback(async (contextMenuRequestedArguments: ContextMenuRequestedArguments) => {
        if (isContextMenuEventTriggered.current) {
            await lazyInitializeNativeContextMenu.importAndExecute(contextMenuRequestedArguments, contextMenuEvent.current);
            isContextMenuEventTriggered.current = false;
        }
        else {
            /**
             * Every invocation of onContextMenuRequested needs to be paired with a call to
             * selectedCommandId on the parameter, otherwise could create unexpected issues with
             * the Native Context Menu commands
             */
            contextMenuRequestedArguments.selectedCommandId = -1;
        }
    }, []);
    
    const onContextMenuEventHandler = useCallback((mouseEvent: MouseEvent) => {
        contextMenuEvent.current = mouseEvent;
        isContextMenuEventTriggered.current = true;
        
        if (extendedHost.current) {
            /**
             * This is the handler for the edge browser native context menu the let us reuse the native
             * context menu commands
             */
            if (shouldDisplayNativeContextMenu(mouseEvent)) {
                if (!isExtendedHostHandlerSet.current) {
                    // This displays the native context menu
                    extendedHost.current.onContextMenuRequested(onContextMenuRequestedHandler);
                    isExtendedHostHandlerSet.current = true;
                }
            }
            else {
                if (isExtendedHostHandlerSet.current) {
                    // This displays the edge browser context menu
                    try {
                        extendedHost.current.onContextMenuRequested(null);
                        isExtendedHostHandlerSet.current = false;
                    }
                    catch { }
                }
            }
        }
    }, []);
    
    useEffect(() => {
        if (isGlobalFeatureEnabled("fwk-contextMenu")) {
            /**
             * Creates a global contextmenu event listener on document so that the native context menu can
             * be displayed on every place in monarch when a user right clicks unless other context menu
             * calls event.StopPropagation() to display their own
             */
            document.addEventListener("contextmenu", onContextMenuEventHandler);
            
            if (extendedHost.current) {
                extendedHost.current.onContextMenuRequested(onContextMenuRequestedHandler);
                isExtendedHostHandlerSet.current = true;
            }
        }
        
        return () => {
            if (isGlobalFeatureEnabled("fwk-contextMenu")) {
                document.removeEventListener("contextmenu", onContextMenuEventHandler);
            }
        };
    }, []);
};

function isInputElement(target: EventTarget | null): target is HTMLInputElement {
    return target !== null && target !== undefined && (target as any).tagName === "INPUT";
}
